import { useState } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { AdminRecord } from '../types';

interface Props {
  admin: AdminRecord;
  onUpdate: () => void;
}

interface PersonalForm {
  first_name: string;
  last_name: string;
  display_name: string;
  cpf: string;
  phone: string;
}

interface AddressForm {
  cep: string;
  street: string;
  street_number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  country: string;
}

const inputCls = 'w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20 transition-colors';
const labelCls = 'block text-[11px] text-white/40 mb-1.5 font-500';

export default function AdminPersonalSection({ admin, onUpdate }: Props) {
  const [personalSaving, setPersonalSaving] = useState(false);
  const [personalMsg, setPersonalMsg] = useState('');
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressMsg, setAddressMsg] = useState('');
  const [cepLoading, setCepLoading] = useState(false);

  const [personal, setPersonal] = useState<PersonalForm>({
    first_name: admin.first_name || '',
    last_name: admin.last_name || '',
    display_name: admin.display_name || '',
    cpf: admin.cpf || '',
    phone: admin.phone || '',
  });

  const [address, setAddress] = useState<AddressForm>({
    cep: admin.cep || '',
    street: admin.street || '',
    street_number: admin.street_number || '',
    complement: admin.complement || '',
    neighborhood: admin.neighborhood || '',
    city: admin.city || '',
    state: admin.state || '',
    country: admin.country || 'Brasil',
  });

  const sp = (f: keyof PersonalForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setPersonal((p) => ({ ...p, [f]: e.target.value }));

  const sa = (f: keyof AddressForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setAddress((p) => ({ ...p, [f]: e.target.value }));

  // Busca automática de endereço pelo CEP (ViaCEP)
  const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    const formatted = raw.length <= 5 ? raw : `${raw.slice(0, 5)}-${raw.slice(5, 8)}`;
    setAddress((prev) => ({ ...prev, cep: formatted }));

    if (raw.length === 8) {
      setCepLoading(true);
      try {
        const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`);
        const data = await res.json();
        if (!data.erro) {
          setAddress((prev) => ({
            ...prev,
            street: data.logradouro || prev.street,
            neighborhood: data.bairro || prev.neighborhood,
            city: data.localidade || prev.city,
            state: data.uf || prev.state,
            country: 'Brasil',
          }));
          setAddressMsg('Endereço preenchido automaticamente!');
          setTimeout(() => setAddressMsg(''), 3000);
        } else {
          setAddressMsg('CEP não encontrado.');
          setTimeout(() => setAddressMsg(''), 3000);
        }
      } catch {
        setAddressMsg('Erro ao buscar CEP.');
        setTimeout(() => setAddressMsg(''), 3000);
      } finally {
        setCepLoading(false);
      }
    }
  };

  const savePersonal = async () => {
    setPersonalSaving(true);
    setPersonalMsg('');
    const fullName = [personal.first_name.trim(), personal.last_name.trim()].filter(Boolean).join(' ');
    const { error } = await supabase.from('profiles').update({
      first_name: personal.first_name.trim() || null,
      last_name: personal.last_name.trim() || null,
      full_name: fullName || null,
      display_name: personal.display_name.trim() || null,
      cpf: personal.cpf.trim() || null,
      phone: personal.phone.trim() || null,
      updated_at: new Date().toISOString(),
    }).eq('id', admin.userId);
    if (!error) { setPersonalMsg('Dados pessoais salvos!'); await onUpdate(); }
    else setPersonalMsg('Erro: ' + error.message);
    setPersonalSaving(false);
    setTimeout(() => setPersonalMsg(''), 3000);
  };

  const saveAddress = async () => {
    setAddressSaving(true);
    setAddressMsg('');
    const { error } = await supabase.from('profiles').update({
      cep: address.cep.replace(/\D/g, '') || null,
      street: address.street.trim() || null,
      street_number: address.street_number.trim() || null,
      complement: address.complement.trim() || null,
      neighborhood: address.neighborhood.trim() || null,
      city: address.city.trim() || null,
      state: address.state.trim() || null,
      country: address.country.trim() || null,
      updated_at: new Date().toISOString(),
    }).eq('id', admin.userId);
    if (!error) { setAddressMsg('Endereço salvo!'); await onUpdate(); }
    else setAddressMsg('Erro: ' + error.message);
    setAddressSaving(false);
    setTimeout(() => setAddressMsg(''), 3000);
  };

  return (
    <div className="space-y-5">
      {/* Dados pessoais */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center bg-violet-600/20 rounded-xl">
              <i className="ri-user-3-line text-violet-400 text-[15px]" />
            </div>
            <h3 className="text-[15px] font-600 text-white">Dados Pessoais</h3>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Nome</label>
            <input value={personal.first_name} onChange={sp('first_name')} placeholder="Nome" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Sobrenome</label>
            <input value={personal.last_name} onChange={sp('last_name')} placeholder="Sobrenome" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Nome de exibição</label>
            <input value={personal.display_name} onChange={sp('display_name')} placeholder="Como aparece no sistema" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Telefone</label>
            <input value={personal.phone} onChange={sp('phone')} placeholder="(11) 99999-9999" className={inputCls} />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>CPF</label>
            <input value={personal.cpf} onChange={sp('cpf')} placeholder="000.000.000-00" className={`${inputCls} max-w-xs`} />
          </div>
        </div>
        <div className="flex items-center justify-between mt-5">
          {personalMsg ? (
            <p className={`text-[12px] ${personalMsg.includes('Erro') ? 'text-red-400' : 'text-emerald-400'} flex items-center gap-1.5`}>
              <i className={personalMsg.includes('Erro') ? 'ri-error-warning-line' : 'ri-check-line'} />{personalMsg}
            </p>
          ) : <span />}
          <button
            onClick={savePersonal}
            disabled={personalSaving}
            className="px-5 py-2 bg-violet-600 hover:bg-violet-700 text-white text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {personalSaving ? <><i className="ri-loader-4-line animate-spin mr-1.5" />Salvando...</> : 'Salvar dados pessoais'}
          </button>
        </div>
      </div>

      {/* Endereço */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-8 h-8 flex items-center justify-center bg-cyan-600/20 rounded-xl">
            <i className="ri-map-pin-2-line text-cyan-400 text-[15px]" />
          </div>
          <h3 className="text-[15px] font-600 text-white">Endereço</h3>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {/* CEP com busca automática */}
          <div className="relative">
            <label className={labelCls}>CEP</label>
            <div className="relative">
              <input
                value={address.cep}
                onChange={handleCepChange}
                placeholder="00000-000"
                maxLength={9}
                className={`${inputCls} pr-8`}
              />
              {cepLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <i className="ri-loader-4-line text-white/40 text-[14px] animate-spin" />
                </div>
              )}
              {!cepLoading && address.cep.replace(/\D/g, '').length === 8 && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <i className="ri-check-line text-emerald-400 text-[14px]" />
                </div>
              )}
            </div>
            <p className="text-[10px] text-white/25 mt-1">Preenchimento automático</p>
          </div>
          <div>
            <label className={labelCls}>Número</label>
            <input value={address.street_number} onChange={sa('street_number')} placeholder="123" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Complemento</label>
            <input value={address.complement} onChange={sa('complement')} placeholder="Apto, sala..." className={inputCls} />
          </div>
          <div className="col-span-3">
            <label className={labelCls}>Logradouro</label>
            <input value={address.street} onChange={sa('street')} placeholder="Preenchido automaticamente pelo CEP" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Bairro</label>
            <input value={address.neighborhood} onChange={sa('neighborhood')} placeholder="Bairro" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Cidade</label>
            <input value={address.city} onChange={sa('city')} placeholder="Cidade" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Estado</label>
            <input value={address.state} onChange={sa('state')} placeholder="SP" className={inputCls} />
          </div>
          <div className="col-span-3">
            <label className={labelCls}>País</label>
            <input value={address.country} onChange={sa('country')} placeholder="Brasil" className={`${inputCls} max-w-xs`} />
          </div>
        </div>
        <div className="flex items-center justify-between mt-5">
          {addressMsg ? (
            <p className={`text-[12px] ${addressMsg.includes('Erro') || addressMsg.includes('não') ? 'text-red-400' : 'text-emerald-400'} flex items-center gap-1.5`}>
              <i className={addressMsg.includes('Erro') || addressMsg.includes('não') ? 'ri-error-warning-line' : 'ri-magic-line'} />{addressMsg}
            </p>
          ) : <span />}
          <button
            onClick={saveAddress}
            disabled={addressSaving}
            className="px-5 py-2 bg-violet-600 hover:bg-violet-700 text-white text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {addressSaving ? <><i className="ri-loader-4-line animate-spin mr-1.5" />Salvando...</> : 'Salvar endereço'}
          </button>
        </div>
      </div>
    </div>
  );
}
